import React, { useMemo, useState } from "react";

interface Order {
  _id: string;
  searchQuery: string;
  amount: number;
  currency?: string;
  trackImage?: string;
  trackArtists?: string[];
  paid?: boolean;
  createdAt: string;
}

interface Props {
  orders: Order[];
}

type SongSort = "total" | "paid" | "selected" | "revenue";
type ArtistSort = "total" | "paid" | "selected" | "revenue";
type PeriodType = "all" | "year" | "month" | "week";

function fmtImg(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Get Monday of a given date's week
function getWeekStart(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function fmtWeek(d: Date): string {
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  return `${MONTHS[d.getMonth()]} ${d.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}

export default function AdminTrendingTab({ orders }: Props) {
  const [songSort, setSongSort] = useState<SongSort>("total");
  const [songDir, setSongDir] = useState<"desc" | "asc">("desc");
  const [artistSort, setArtistSort] = useState<ArtistSort>("total");
  const [artistDir, setArtistDir] = useState<"desc" | "asc">("desc");
  const [periodType, setPeriodType] = useState<PeriodType>("all");
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedWeek, setSelectedWeek] = useState<string>("");

  // Build available years, months, weeks from data
  const { availableYears, availableMonths, availableWeeks } = useMemo(() => {
    const years = new Set<number>();
    const monthSet = new Set<string>();
    const weekMap = new Map<string, Date>();

    for (const o of orders) {
      const d = new Date(o.createdAt);
      years.add(d.getFullYear());
      monthSet.add(`${d.getFullYear()}-${d.getMonth()}`);
      const ws = getWeekStart(d);
      const wKey = ws.toISOString().slice(0, 10);
      if (!weekMap.has(wKey)) weekMap.set(wKey, ws);
    }

    const sortedYears = Array.from(years).sort((a, b) => b - a);
    const sortedMonths = Array.from(monthSet)
      .map((k) => { const [y, m] = k.split("-"); return { year: parseInt(y), month: parseInt(m), key: k }; })
      .sort((a, b) => b.year - a.year || b.month - a.month);
    const sortedWeeks = Array.from(weekMap.entries())
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([key, date]) => ({ key, date, label: fmtWeek(date) }));

    return { availableYears: sortedYears, availableMonths: sortedMonths, availableWeeks: sortedWeeks };
  }, [orders]);

  // Auto-select first option when switching period type
  const handlePeriodType = (type: PeriodType) => {
    setPeriodType(type);
    if (type === "year" && !selectedYear && availableYears.length > 0) setSelectedYear(String(availableYears[0]));
    if (type === "month" && !selectedMonth && availableMonths.length > 0) setSelectedMonth(availableMonths[0].key);
    if (type === "week" && !selectedWeek && availableWeeks.length > 0) setSelectedWeek(availableWeeks[0].key);
  };

  // Filter orders by period
  const filteredOrders = useMemo(() => {
    if (periodType === "all") return orders;

    if (periodType === "year" && selectedYear) {
      const year = parseInt(selectedYear);
      return orders.filter((o) => new Date(o.createdAt).getFullYear() === year);
    }

    if (periodType === "month" && selectedMonth) {
      const [y, m] = selectedMonth.split("-").map(Number);
      return orders.filter((o) => {
        const d = new Date(o.createdAt);
        return d.getFullYear() === y && d.getMonth() === m;
      });
    }

    if (periodType === "week" && selectedWeek) {
      const weekStart = new Date(selectedWeek);
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      return orders.filter((o) => {
        const d = new Date(o.createdAt);
        return d >= weekStart && d < weekEnd;
      });
    }

    return orders;
  }, [orders, periodType, selectedYear, selectedMonth, selectedWeek]);

  // Song aggregation
  const trendingSongs = useMemo(() => {
    const map = new Map<string, {
      query: string; artists: string[]; image: string | null;
      paidCount: number; selectedCount: number; revenue: number;
    }>();

    for (const o of filteredOrders) {
      const key = o.searchQuery.trim().toLowerCase();
      if (!key) continue;
      const isPaid = !!o.paid;
      const existing = map.get(key);
      if (existing) {
        if (isPaid) { existing.paidCount++; existing.revenue += o.amount || 0; }
        else { existing.selectedCount++; }
        if (!existing.image && o.trackImage) existing.image = o.trackImage;
      } else {
        map.set(key, {
          query: o.searchQuery, artists: o.trackArtists || [], image: o.trackImage || null,
          paidCount: isPaid ? 1 : 0, selectedCount: isPaid ? 0 : 1,
          revenue: isPaid ? (o.amount || 0) : 0,
        });
      }
    }

    const list = Array.from(map.values()).filter((s) => s.paidCount + s.selectedCount > 2);
    list.sort((a, b) => {
      let cmp = 0;
      if (songSort === "total") cmp = (a.paidCount + a.selectedCount) - (b.paidCount + b.selectedCount);
      else if (songSort === "paid") cmp = a.paidCount - b.paidCount;
      else if (songSort === "selected") cmp = a.selectedCount - b.selectedCount;
      else if (songSort === "revenue") cmp = a.revenue - b.revenue;
      return songDir === "desc" ? -cmp : cmp;
    });
    return list.slice(0, 20);
  }, [filteredOrders, songSort, songDir]);

  // Artist aggregation
  const topArtists = useMemo(() => {
    const map = new Map<string, { name: string; paidCount: number; selectedCount: number; revenue: number }>();

    for (const o of filteredOrders) {
      if (!o.trackArtists) continue;
      const isPaid = !!o.paid;
      for (const artist of o.trackArtists) {
        const key = artist.trim().toLowerCase();
        if (!key) continue;
        const existing = map.get(key);
        if (existing) {
          if (isPaid) { existing.paidCount++; existing.revenue += o.amount || 0; }
          else { existing.selectedCount++; }
        } else {
          map.set(key, { name: artist, paidCount: isPaid ? 1 : 0, selectedCount: isPaid ? 0 : 1, revenue: isPaid ? (o.amount || 0) : 0 });
        }
      }
    }

    const list = Array.from(map.values());
    list.sort((a, b) => {
      let cmp = 0;
      if (artistSort === "total") cmp = (a.paidCount + a.selectedCount) - (b.paidCount + b.selectedCount);
      else if (artistSort === "paid") cmp = a.paidCount - b.paidCount;
      else if (artistSort === "selected") cmp = a.selectedCount - b.selectedCount;
      else if (artistSort === "revenue") cmp = a.revenue - b.revenue;
      return artistDir === "desc" ? -cmp : cmp;
    });
    return list.slice(0, 20);
  }, [filteredOrders, artistSort, artistDir]);

  const toggleSongSort = (field: SongSort) => {
    if (songSort === field) setSongDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSongSort(field); setSongDir("desc"); }
  };
  const toggleArtistSort = (field: ArtistSort) => {
    if (artistSort === field) setArtistDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setArtistSort(field); setArtistDir("desc"); }
  };

  const sortArrow = (active: boolean, dir: "desc" | "asc") => {
    if (!active) return <span className="muted-text" style={{ marginLeft: 2 }}>↕</span>;
    return <span style={{ marginLeft: 2 }}>{dir === "desc" ? "↓" : "↑"}</span>;
  };

  // Period label
  const periodLabel = periodType === "all" ? "All Time"
    : periodType === "year" ? selectedYear
    : periodType === "month" && selectedMonth ? `${MONTHS[parseInt(selectedMonth.split("-")[1])]} ${selectedMonth.split("-")[0]}`
    : periodType === "week" && selectedWeek ? availableWeeks.find((w) => w.key === selectedWeek)?.label || selectedWeek
    : "All Time";

  return (
    <div className="adm-tab-container fade-in">
      {/* Period filter */}
      <div className="adm-toolbar" style={{ marginBottom: 14 }}>
        <div className="adm-toolbar-group">
          {(["all", "year", "month", "week"] as PeriodType[]).map((t) => (
            <button
              key={t}
              className={`filter-chip ${periodType === t ? "active" : ""}`}
              onClick={() => handlePeriodType(t)}
            >
              {t === "all" ? "All Time" : t === "year" ? "Year" : t === "month" ? "Month" : "Week"}
            </button>
          ))}
          {periodType === "year" && (
            <select className="adm-select" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
              {availableYears.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            </select>
          )}
          {periodType === "month" && (
            <select className="adm-select" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
              {availableMonths.map((m) => <option key={m.key} value={m.key}>{MONTHS[m.month]} {m.year}</option>)}
            </select>
          )}
          {periodType === "week" && (
            <select className="adm-select" value={selectedWeek} onChange={(e) => setSelectedWeek(e.target.value)}>
              {availableWeeks.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
            </select>
          )}
        </div>
        <span className="muted-text" style={{ fontSize: "0.76rem" }}>
          {filteredOrders.length} orders · {periodLabel}
        </span>
      </div>

      {/* Trending Songs */}
      <div className="adm-card" style={{ marginBottom: 16 }}>
        <h2 className="adm-section-title" style={{ marginBottom: 12 }}>Trending Songs</h2>
        <div className="adm-scrollable-full" style={{ maxHeight: 520 }}>
          <table className="adm-table-light adm-table-compact sticky-header" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "28%" }}>Track</th>
                <th style={{ width: "18%" }}>Artist</th>
                <th style={{ width: "10%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSongSort("paid")}>
                  Paid {sortArrow(songSort === "paid", songDir)}
                </th>
                <th style={{ width: "10%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSongSort("selected")}>
                  Selected {sortArrow(songSort === "selected", songDir)}
                </th>
                <th style={{ width: "10%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSongSort("total")}>
                  Total {sortArrow(songSort === "total", songDir)}
                </th>
                <th style={{ width: "12%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleSongSort("revenue")}>
                  Revenue {sortArrow(songSort === "revenue", songDir)}
                </th>
              </tr>
            </thead>
            <tbody>
              {trendingSongs.map((s, i) => {
                const img = fmtImg(s.image ?? undefined);
                const total = s.paidCount + s.selectedCount;
                return (
                  <tr key={`song-${i}`} className="hover-row">
                    <td className="muted-text" style={{ fontWeight: 600 }}>{i + 1}</td>
                    <td>
                      <div className="dj-cell dj-cell-xs">
                        {img ? (
                          <img src={img} className="track-img-xs" alt="" />
                        ) : (
                          <div className="track-placeholder-xs">♪</div>
                        )}
                        <span className="truncate-inline" style={{ fontWeight: 600, fontSize: "0.82rem" }}>
                          {s.query}
                        </span>
                      </div>
                    </td>
                    <td className="muted-text" style={{ fontSize: "0.78rem" }}>
                      {s.artists.join(", ") || "—"}
                    </td>
                    <td style={{ fontWeight: 700, color: "var(--adm-green)" }}>{s.paidCount}</td>
                    <td style={{ fontWeight: 600, color: "var(--adm-orange)" }}>{s.selectedCount > 0 ? s.selectedCount : "—"}</td>
                    <td style={{ fontWeight: 700 }}>{total}</td>
                    <td style={{ fontWeight: 600, color: "var(--adm-green)" }}>${s.revenue.toFixed(2)}</td>
                  </tr>
                );
              })}
              {trendingSongs.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>
                    No songs with 3+ requests {periodType !== "all" ? `in ${periodLabel}` : "yet"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Artists */}
      <div className="adm-card">
        <h2 className="adm-section-title" style={{ marginBottom: 12 }}>Top Artists</h2>
        <div className="adm-scrollable-full" style={{ maxHeight: 480 }}>
          <table className="adm-table-light adm-table-compact sticky-header" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "30%" }}>Artist</th>
                <th style={{ width: "12%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleArtistSort("paid")}>
                  Paid {sortArrow(artistSort === "paid", artistDir)}
                </th>
                <th style={{ width: "12%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleArtistSort("selected")}>
                  Selected {sortArrow(artistSort === "selected", artistDir)}
                </th>
                <th style={{ width: "12%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleArtistSort("total")}>
                  Total {sortArrow(artistSort === "total", artistDir)}
                </th>
                <th style={{ width: "15%", cursor: "pointer", userSelect: "none" }} onClick={() => toggleArtistSort("revenue")}>
                  Revenue {sortArrow(artistSort === "revenue", artistDir)}
                </th>
              </tr>
            </thead>
            <tbody>
              {topArtists.map((a, i) => {
                const total = a.paidCount + a.selectedCount;
                return (
                  <tr key={`artist-${i}`} className="hover-row">
                    <td className="muted-text" style={{ fontWeight: 600 }}>{i + 1}</td>
                    <td style={{ fontWeight: 600, fontSize: "0.82rem" }}>{a.name}</td>
                    <td style={{ fontWeight: 700, color: "var(--adm-green)" }}>{a.paidCount}</td>
                    <td style={{ fontWeight: 600, color: "var(--adm-orange)" }}>{a.selectedCount > 0 ? a.selectedCount : "—"}</td>
                    <td style={{ fontWeight: 700 }}>{total}</td>
                    <td style={{ fontWeight: 600, color: "var(--adm-green)" }}>${a.revenue.toFixed(2)}</td>
                  </tr>
                );
              })}
              {topArtists.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>
                    No artist data {periodType !== "all" ? `in ${periodLabel}` : "yet"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
